/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  IN_PROGRESS: "inProgress",
  SUCCESS: "success",
  COMPLETE: "complete",
  CONDITION_FAILED: "conditionFailed",
  ERROR_LIGHTING_DRIVE: "errorLightingDrive",
  ERROR_AXIS_ADJUSTMENT: "errorAxisAdjustment",
  FAILED_AXIS_ADJUSTMENT: "failedAxisAdjustment",
  ERROR: "error"
};

/** API type定義 */
const API_TYPE = {
  STANDBY: 'standby',
  START: 'start',
  TERMINATE: 'terminate',
  DISPOSE: 'dispose'
};

/**
 * 軸調整失敗時にRoutine statusによって表示内容を変えるため判定用に定義
 */
const ROUTINE_STATUS = {
  STATUS_09: '09',
  STATUS_0C: '0C',
  STATUS_0D: '0D'
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: "requirement067",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      displayInfo: '',
      modal_requirement_067: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED_AXIS_ADJUSTMENT:
        case PHASE.CONDITION_FAILED:
        case PHASE.ERROR_LIGHTING_DRIVE:
        case PHASE.ERROR_AXIS_ADJUSTMENT:
        case PHASE.ERROR:
          return "danger";
        case PHASE.SUCCESS:
        case PHASE.COMPLETE:
          return "success";
        case PHASE.START:
        case PHASE.IN_PROGRESS:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_067 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_067 = false;
    },

    /** 終了処理 */
    exit() {
      // 画面を閉じる
      this.closeWizard();

      // 初期画面に戻す
      this.phase = PHASE.START;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー画面の場合は、ウィザードを閉じる
      if (this.phase === PHASE.ERROR) {
        this.exit();
        return;
      }

      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    // API実行-------------------------------------------

    /** 待機 */
    async standby() {
      // API実行パラメータ作成
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        type: API_TYPE.STANDBY,
      };

      // 初期処理（要求）
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        requestBody,
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      let isSuccess = false;

      if (response) {
        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            // 現在セッション更新（NonDefaultSession）
            this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
            isSuccess = true;
            break;
          case config.STATUS_WSP_FAILED:
            // 共通エラー画面へ遷移
            this.showUnexpectedError();
            break;
          default:
            this.showUnexpectedError();
            break;
        }
      }
      return isSuccess;
    },

    /** レーダ軸調整実施 */
    async execute() {

      // 実行監視画面へ遷移
      this.phase = PHASE.IN_PROGRESS;

      // API実行パラメータ作成
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        type: API_TYPE.START,
      };

      // 初期処理（要求）
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        requestBody,
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      if (response) {
        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            this.showSuccessData(response.data.user_data);
            this.phase = PHASE.SUCCESS;
            break;
          case config.STATUS_WSP_FAILED:
            // 失敗
            // 各エラー表示
            switch (response.data.async_code) {
              case error.WSP_067_CONDITION_FAILED:
                // 条件不成立
                this.phase = PHASE.CONDITION_FAILED;
                break;
              case error.WSP_067_LIGHTING_DRIVE_ERROR:
                // 通信エラー(ミラー駆動指示)
                this.phase = PHASE.ERROR_LIGHTING_DRIVE;
                break;
              case error.WSP_067_AXIS_ADJUSTMENT_INSTRUCTIONS:
                // 通信エラー(軸調整指示)
                this.phase = PHASE.ERROR_AXIS_ADJUSTMENT;
                break;
              case error.WSP_067_AXIS_ADJUSTMENT_FAILED:
                // 軸調整失敗表示
                this.showFailedData(response.data.user_data.routine_status);
                this.phase = PHASE.FAILED_AXIS_ADJUSTMENT;
                break;
              default:
                // 該当のエラーコードが存在しない場合、共通エラー
                // ありえないが定義しておく
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            this.showUnexpectedError();
            break;
        }
      }
    },

    /** DefaultSession遷移時の処理 */
    async terminate() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.TERMINATE,
        };

        // terminate API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              // 現在セッション更新（DefaultSession）
              this.sessionProperty = SESSION_PROPERTY.DEFAULT;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              break;
          }
          // disposeAPIを実施して機能終了する
          await this.dispose();
          this.exit();
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了時の処理 */
    async dispose() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.DISPOSE
        };

        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

      } finally {
        this.isLoading = false;
      }
    },

    // 表示内容の設定-------------------------------------------

    /**
     * 軸調整成功表示で表示する内容を編集
     * @param {object} responseData データ変換済みのルーチン結果 (ターゲット反射強度,（RCS）ターゲット角度位置)
     */
    showSuccessData(responseData) {
      this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_67_TXT_3_00`)
        .replace('$ROUTINESTATUS$', responseData.angular_position)
        .replace('$ROUTINESTATUS$', responseData.reflective_intensity);
    },

    /**
     * 軸調整失敗表示での表示内容を設定
     * @param {String} responseRoutinestatus ルーチン結果 (Routine Status)
     */
    showFailedData(responseRoutinestatus) {
      // routine_statusによって軸調整失敗時の表示内容を変更する
      switch (responseRoutinestatus) {
        case ROUTINE_STATUS.STATUS_09:
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_67_TXT_5_09`);
          break;
        case ROUTINE_STATUS.STATUS_0C:
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_67_TXT_5_0C`);
          break;
        case ROUTINE_STATUS.STATUS_0D:
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_67_TXT_5_0D`);
          break;
        default:
          break;
      }
    },

    // ユーザ操作-------------------------------------------

    /** 初期画面 OK押下処理 */
    async onStartOkClicked() {
      try {
        this.isLoading = true;

        // 待機処理
        if (!await this.standby()) {
          return;
        }

        // 実行処理
        await this.execute();

      } finally {
        this.isLoading = false;
      }
    },

    /** 初期画面 キャンセル押下処理 */
    onStartCancelClicked() {
      this.exit();
    },

    /** 成功画面 OK押下処理 */
    onSuccessOkClicked() {
      this.phase = PHASE.COMPLETE;
    },

    /** 完了画面 OK押下処理 */
    onCompleteOkClicked() {
      this.terminate();
    },

    /** 異常系画面 OK押下処理 */
    onFailedOkClicked() {
      this.terminate();
    },

    /** 共通エラー画面 OK押下処理 */
    onCommonFailedOkClicked() {
      // 現在セッションがNonDefaultSessionの場合はterminateを実行する
      if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
        this.terminate();
      } else {
        this.dispose();
        this.exit();
      }
    }
  },

  async mounted() {
    this.init();
  }
};
